import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import ReactCrop from 'react-image-crop'
import { FormattedMessage } from 'react-intl'
import { Dialog, DialogContent, DialogActions, Button, DialogTitle } from '@mui/material'
import 'react-image-crop/dist/ReactCrop.css'

interface Props {
  name: string
  open: boolean
  src: string
  imageType: string
  width: number
  height: number
  circularImage: boolean
  handleUpload: (image: File) => void
  handleClose: () => void
}

export default function ImageCropDialog({
  name,
  open,
  src,
  imageType,
  width,
  height,
  circularImage,
  handleUpload,
  handleClose,
}: Props): JSX.Element {
  const [crop, setCrop] = useState<any>({ unit: '%', aspect: width / height, height: 80 })
  const imgRef = useRef<HTMLImageElement | null>(null)

  const handleCompleted = async () => {
    const file = await getCroppedImage(imgRef.current, crop, name)
    handleUpload(file)
    handleClose()
  }

  const getImageType = () => {
    return circularImage ? 'image/png' : imageType
  }

  const getCroppedImage = (image, crop, fileName): any => {
    const canvas = document.createElement('canvas')
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')

    if (ctx) {
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        width,
        height,
      )

      if (circularImage) {
        ctx.globalCompositeOperation = 'destination-in'
        ctx.beginPath()
        ctx.arc(width / 2, height / 2, height / 2, 0, Math.PI * 2)
        ctx.closePath()
        ctx.fill()
      }

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            console.error('Canvas is empty')
            return
          }
          URL.revokeObjectURL(image)
          ;(blob as any).name = fileName
          resolve(blob)
        }, getImageType())
      })
    }
  }

  return (
    <CropDialog
      maxWidth={'md'}
      open={open}
      scroll={'body'}
      fullWidth={true}
      keepMounted={false}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose()
        }
      }}
    >
      <DialogTitle id={'crop-image-dialog-title'}>
        <FormattedMessage id="strings.cropImage" />
      </DialogTitle>

      <DialogContent>
        <CropContainer>
          {src && (
            <ReactCrop crop={crop} onChange={setCrop} circularCrop={circularImage}>
              <img src={src} ref={imgRef} />
            </ReactCrop>
          )}
        </CropContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          <FormattedMessage id={'strings.close'} />
        </Button>
        <Button onClick={handleCompleted} color="primary" variant={'contained'}>
          <FormattedMessage id={'forms.save'} />
        </Button>
      </DialogActions>
    </CropDialog>
  )
}

const CropDialog = styled(Dialog)`
  z-index: 99999 !important;
`

const CropContainer = styled.div`
  text-align: center;
  background: ${(props) => props.theme.customPalette.lightGray};
  padding: 1em;
`
